/**
 * Utility for constructing sized arche icon URLs.
 *
 * Icons are stored in S3 at multiple sizes:
 *   {archId}/icon.png      — 1024px original
 *   {archId}/icon-512.png  — 512px
 *   {archId}/icon-256.png  — 256px
 *   {archId}/icon-128.png  — 128px
 *   {archId}/icon-64.png   — 64px
 */

import { useState, type ImgHTMLAttributes } from 'react';

export type IconSize = 64 | 128 | 256 | 512;

/**
 * Derive a sized icon URL from the original image_ref.
 * Sized variants are always PNG (generated by sharp), so the extension is
 * replaced with .png regardless of the original format (jpg, webp, etc.).
 * Returns the original URL if no size is specified or the URL doesn't match the expected pattern.
 */
export function archeIconUrl(imageRef: string | null | undefined, size?: IconSize): string | null {
  if (!imageRef) return null;
  if (!size) return imageRef;
  const replaced = imageRef.replace(/\/icon\.\w+$/, `/icon-${size}.png`);
  // If the replacement didn't change anything, the URL doesn't match our pattern — return original
  return replaced === imageRef ? imageRef : replaced;
}

/**
 * Default icon shown when an arche has no uploaded icon or the image fails to load.
 * Renders a gradient background with a 2×2 grid motif suggesting "modules".
 */
export function DefaultArcheIcon({
  className,
  ...props
}: Omit<React.HTMLAttributes<HTMLDivElement>, 'children'>) {
  return (
    <div
      className={`bg-linear-to-br from-blue-400 via-blue-500 to-blue-700 flex items-center justify-center ${className ?? ''}`}
      {...props}
    >
      <svg viewBox="0 0 24 24" fill="none" className="w-1/2 h-1/2" aria-hidden="true">
        <rect x="2" y="2" width="9" height="9" rx="2" fill="white" opacity="0.35" />
        <rect x="13" y="2" width="9" height="9" rx="2" fill="white" opacity="0.35" />
        <rect x="2" y="13" width="9" height="9" rx="2" fill="white" opacity="0.35" />
        <rect x="13" y="13" width="9" height="9" rx="2" fill="white" opacity="0.35" />
      </svg>
    </div>
  );
}

/**
 * Props for ArcheIconImg — a sized arche icon with automatic fallback.
 */
export interface ArcheIconImgProps extends Omit<ImgHTMLAttributes<HTMLImageElement>, 'src'> {
  imageRef: string;
  size: IconSize;
  /** Single-character fallback (shown when image fails to load) — ignored, kept for API compat */
  fallbackLetter?: string;
}

/**
 * Renders a sized arche icon <img> that falls back to the original URL on error,
 * then to a DefaultArcheIcon placeholder if both URLs fail.
 */
export function ArcheIconImg({
  imageRef,
  size,
  fallbackLetter: _,
  ...imgProps
}: ArcheIconImgProps) {
  const [failed, setFailed] = useState(false);

  if (failed) {
    return (
      <DefaultArcheIcon
        className={imgProps.className ?? ''}
        role="img"
        aria-label={imgProps.alt ?? ''}
      />
    );
  }

  return (
    <img
      src={archeIconUrl(imageRef, size) ?? ''}
      onError={(e) => {
        // First try the original (unsized) URL
        if (e.currentTarget.src !== imageRef) {
          e.currentTarget.src = imageRef;
        } else {
          // Both sized and original failed — show default icon
          setFailed(true);
        }
      }}
      {...imgProps}
    />
  );
}
